import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Lock, Mail, KeyRound, Check, Circle, Clock, AlertTriangle } from 'lucide-react';
import AuthShell, { authInputCls, authButtonCls } from '../components/auth/AuthShell';
import { useAuth } from '../context/AuthContext';
import { api, ApiError } from '../lib/api';
import { PASSWORD_RULES, meetsPasswordPolicy, landingForRole } from '../lib/passwordPolicy';

// /set-password#t=<token> — where an invite or reset email lands.
//
// The token is in the URL fragment so it never reaches a server log or a
// Referer header. We read it once, then strip it from the address bar.
// Opening this page does NOT use the link up (mail scanners open every link);
// only submitting a password does.

const DEAD_LINK = {
  expired: { title: 'This link has expired', body: 'For your security, links stop working after a while. Request a new one and use it soon after it arrives.' },
  used: { title: 'This link has already been used', body: 'Your password is set. Sign in with it, or request a new link if you have forgotten it.' },
  revoked: { title: 'A newer link was sent', body: 'Only the most recent link for an account works. Use the newest email, or request another link.' },
  not_found: { title: 'This link isn’t valid', body: 'Check that you opened the whole link from the email. If it still doesn’t work, request a new one.' },
  inactive: { title: 'This account is disabled', body: 'Contact support@nexsav.net if you think this is a mistake.' },
};

function readTokenFromHash() {
  if (typeof window === 'undefined') return '';
  const m = (window.location.hash || '').match(/[#&]t=([A-Za-z0-9_-]+)/);
  return m ? m[1] : '';
}

export default function SetPassword() {
  const { refresh } = useAuth();
  const navigate = useNavigate();
  const tokenRef = useRef(readTokenFromHash());
  const [state, setState] = useState(tokenRef.current ? 'loading' : 'not_found');
  const [info, setInfo] = useState(null); // { purpose, email, name }
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    // Remove the token from the address bar (and history) as soon as we have it.
    if (tokenRef.current && window.location.hash) {
      window.history.replaceState(window.history.state, '', window.location.pathname + window.location.search);
    }
    if (!tokenRef.current) return undefined;
    let cancelled = false;
    api.post('/api/auth/password-link/check', { token: tokenRef.current })
      .then((res) => {
        if (cancelled) return;
        setInfo(res);
        setState(res?.state || 'not_found');
      })
      .catch(() => { if (!cancelled) setState('error'); });
    return () => { cancelled = true; };
  }, []);

  const rules = useMemo(() => PASSWORD_RULES.map((r) => ({ ...r, ok: r.test(password) })), [password]);
  const mismatch = confirm.length > 0 && confirm !== password;
  const canSubmit = meetsPasswordPolicy(password) && confirm === password && !submitting;
  const invite = info?.purpose === 'invite';
  const firstName = String(info?.name || '').trim().split(/\s+/)[0];

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setError('');
    try {
      const res = await api.post('/api/auth/password-link/set', { token: tokenRef.current, password });
      tokenRef.current = '';
      await refresh();
      navigate(landingForRole(res?.user?.role), { replace: true });
    } catch (err) {
      if (err instanceof ApiError && err.status === 410 && err.data?.state) {
        setState(err.data.state);
      } else if (err instanceof ApiError) {
        setError(err.message || 'Could not save your password.');
      } else {
        setError('Network error, please try again.');
      }
      setSubmitting(false);
    }
  };

  if (state === 'loading') {
    return (
      <AuthShell badge="Portal account" badgeIcon={KeyRound} title="Checking your link…" backTo={null}>
        <div className="flex justify-center py-4" role="status" aria-live="polite">
          <div className="w-5 h-5 border-2 border-[#B0FC38] border-t-transparent rounded-full animate-spin" aria-hidden="true" />
          <span className="sr-only">Checking your link</span>
        </div>
      </AuthShell>
    );
  }

  if (state !== 'valid') {
    const dead = DEAD_LINK[state] || { title: 'Something went wrong', body: 'We couldn’t check this link. Refresh the page, or request a new link.' };
    return (
      <AuthShell badge="Link not usable" badgeIcon={state === 'expired' ? Clock : AlertTriangle} title={dead.title} subtitle={dead.body}>
        <div className="flex flex-col gap-3" data-testid={`link-state-${state}`}>
          {state !== 'inactive' && (
            <Link to="/forgot-password" className={authButtonCls}>Request a new link</Link>
          )}
          <Link to="/login" className="text-center text-sm text-gray-400 hover:text-white transition-colors">Go to sign in</Link>
          {state === 'expired' && invite && (
            <p className="text-center text-[12px] text-gray-500">An expired invite can be renewed from “Request a new link” with the same email address.</p>
          )}
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      badge={invite ? 'Welcome to NexSav' : 'Reset password'}
      badgeIcon={KeyRound}
      title={invite ? <>Hi {firstName || 'there'}, choose your <span className="serif-acc accent-text">password</span>.</> : <>Choose a new <span className="serif-acc accent-text">password</span>.</>}
      subtitle={invite ? 'You’ll use it with your email to sign in to the NexSav portal.' : 'Once it’s saved, you’ll be signed out everywhere else.'}
      backTo={null}
    >
      <form onSubmit={onSubmit} className="flex flex-col gap-5" noValidate>
        <div>
          <label htmlFor="sp-email" className="block text-xs font-medium text-gray-400/80 mb-2 tracking-wide">Email</label>
          <div className="relative">
            <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-600 pointer-events-none" aria-hidden="true" />
            {/* Read-only, but present so password managers save the right username. */}
            <input id="sp-email" type="email" autoComplete="username" readOnly value={info?.email || ''} className={`${authInputCls} text-gray-300`} />
          </div>
        </div>

        <div>
          <label htmlFor="sp-password" className="block text-xs font-medium text-gray-400/80 mb-2 tracking-wide">New password</label>
          <div className="relative">
            <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-600 pointer-events-none" aria-hidden="true" />
            <input
              id="sp-password"
              type="password"
              autoComplete="new-password"
              autoFocus
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              aria-describedby="sp-rules"
              className={authInputCls}
            />
          </div>
          <ul id="sp-rules" className="mt-3 grid grid-cols-2 gap-x-3 gap-y-1.5" aria-label="Password requirements">
            {rules.map((r) => (
              <li key={r.id} className={`flex items-center gap-1.5 text-[12px] ${r.ok ? 'text-[#B0FC38]' : 'text-gray-500'}`} data-testid={`rule-${r.id}`} data-ok={r.ok}>
                {r.ok ? <Check className="w-3.5 h-3.5" aria-hidden="true" /> : <Circle className="w-3 h-3" aria-hidden="true" />}
                <span>{r.label}<span className="sr-only">{r.ok ? ' (met)' : ' (not yet)'}</span></span>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <label htmlFor="sp-confirm" className="block text-xs font-medium text-gray-400/80 mb-2 tracking-wide">Confirm password</label>
          <div className="relative">
            <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-600 pointer-events-none" aria-hidden="true" />
            <input
              id="sp-confirm"
              type="password"
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              aria-invalid={mismatch || undefined}
              aria-describedby={mismatch ? 'sp-confirm-error' : undefined}
              className={authInputCls}
            />
          </div>
          {mismatch && <p id="sp-confirm-error" className="mt-1 text-[12px] text-red-400">Passwords don’t match.</p>}
        </div>

        {error && (
          <div className="px-4 py-3 rounded-lg bg-red-500/[0.08] border border-red-500/20 text-sm text-red-300/90" role="alert">{error}</div>
        )}

        <button type="submit" disabled={!canSubmit} className={`mt-1 ${authButtonCls}`}>
          {submitting ? (
            <>
              <span className="w-4 h-4 border-2 border-gray-900 border-t-transparent rounded-full animate-spin" aria-hidden="true" />
              Saving…
            </>
          ) : (
            invite ? 'Set password and sign in' : 'Save password and sign in'
          )}
        </button>
      </form>
    </AuthShell>
  );
}
